import { IconForAttachmentCitation } from "@app/components/assistant/conversation/attachment/utils";
import { useConversationSidePanelContext } from "@app/components/assistant/conversation/ConversationSidePanelContext";
import type { DocumentTone } from "@app/components/assistant/conversation/cards/documentTone";
import { documentToneClassNames } from "@app/components/assistant/conversation/cards/documentTone";
import { getArtifactTone } from "@app/components/assistant/conversation/cards/GeneratedArtifactCard";
import {
  DetailsChip,
  DetailsCollapse,
  DetailsEmptyState,
  DetailsRow,
  DetailsSection,
} from "@app/components/assistant/conversation/details_panel/DetailsSection";
import {
  groupSourcesByProvider,
  isToolOutputPath,
  type SourceGroup,
} from "@app/components/assistant/conversation/details_panel/utils";
import type { ConversationAttachmentItem } from "@app/components/assistant/conversation/files_panel/types";
import { getSingularFileCategoryLabelForContentType } from "@app/components/file_explorer/utils";
import { useOpenConversationLiveDoc } from "@app/components/live/useOpenConversationLiveDoc";
import { useTheme } from "@app/components/sparkle/ThemeContext";
import { useConversationAttachments } from "@app/hooks/conversations/useConversationAttachments";
import { useConversationDetails } from "@app/hooks/conversations/useConversationDetails";
import { useConversationSandboxFiles } from "@app/hooks/conversations/useConversationSandboxFiles";
import {
  isContentNodeAttachmentType,
  isFileAttachmentType,
} from "@app/lib/api/assistant/conversation/attachments";
import { getFileTypeIcon } from "@app/lib/file_icon_utils";
import { getSkillIcon } from "@app/lib/skill";
import { useChatFiles } from "@app/lib/swr/chats";
import { useLiveDocs } from "@app/lib/swr/live_docs";
import type {
  ConversationBrowsedPageType,
  ConversationDetailsSkillType,
  ConversationSourceType,
} from "@app/types/api/assistant/conversation/details";
import type { FileSystemFileEntry } from "@app/types/api/file_system/types";
import type { ConversationWithoutContentType } from "@app/types/assistant/conversation";
import {
  getFrameDisplayTitle,
  isInteractiveContentType,
} from "@app/types/files";
import type { LiveDocKind, LiveDocType } from "@app/types/live_docs";
import {
  assertNever,
  assertNeverAndIgnore,
} from "@app/types/shared/utils/assert_never";
import type { LightWorkspaceType } from "@app/types/user";
import {
  BookOpen01,
  Browser,
  Button,
  FaviconIcon,
  File06,
  Icon,
  LayoutRight,
  Spinner,
  Stars01,
} from "@dust-tt/sparkle";
import { DesktopPreview, liveDocIcon, liveDocKindLabel } from "@memo-air/react";
import moment from "moment";
import { useMemo, useState } from "react";

const faviconStackSize = 3;
const urlProtocolPattern = /^https?:\/\/(www\.)?/;

type DetailsFileItem = {
  key: string;
  title: string;
  contentType: string;
  date: number | null;
  liveDocKind?: LiveDocKind;
  onClick?: () => void;
};

interface ConversationDetailsPanelProps {
  conversation: ConversationWithoutContentType;
  owner: LightWorkspaceType;
}

export function ConversationDetailsPanel({
  conversation,
  owner,
}: ConversationDetailsPanelProps) {
  const { closePanel, openPanel } = useConversationSidePanelContext();
  const { sources, skills, browsedPages, isConversationDetailsLoading } =
    useConversationDetails({ conversationId: conversation.sId, owner });
  const { files, workingFiles, isFilesLoading } = useDetailsFiles({
    conversation,
    owner,
  });
  const { liveDocs, isLiveDocsLoading } = useLiveDocs({ owner });
  const conversationLiveDocs = useMemo(
    () => liveDocs.filter((doc) => doc.conversationId === conversation.sId),
    [liveDocs, conversation.sId]
  );
  const openSpace = useOpenConversationLiveDoc({
    owner,
    conversationId: conversation.sId,
    kind: "space",
    title: conversation.title ?? "Space",
  });
  const filesWithLiveDocs = useMemo(
    () => [
      ...conversationLiveDocs
        .filter((doc) => doc.kind !== "space")
        .map(
          (doc): DetailsFileItem => ({
            key: doc.sId,
            title: doc.title,
            contentType: "",
            date: doc.updatedAt,
            liveDocKind: doc.kind,
            onClick: () => openPanel({ type: "live_doc", docId: doc.sId }),
          })
        ),
      ...files,
    ],
    [conversationLiveDocs, files, openPanel]
  );

  return (
    <div className="sheet flex h-panel flex-col bg-panel-background animate-in fade-in-0 slide-in-from-left-2 duration-300 ease-out motion-reduce:animate-none">
      <div className="sheet-header relative z-10 flex h-title shrink-0 items-center justify-between bg-panel-background px-4">
        <h2 className="text-base font-semibold text-foreground">
          In this chat
        </h2>
        <Button
          variant="ghost"
          size="sm"
          icon={LayoutRight}
          tooltip="Hide chat details"
          aria-label="Hide chat details"
          onClick={closePanel}
        />
      </div>
      <div className="flex min-h-0 flex-1 flex-col divide-y divide-border overflow-y-auto px-4 pb-4 pt-2">
        {isConversationDetailsLoading || isFilesLoading || isLiveDocsLoading ? (
          <div className="flex flex-1 items-center justify-center py-10">
            <Spinner />
          </div>
        ) : (
          <>
            <FilesSection
              files={filesWithLiveDocs}
              workingFiles={workingFiles}
            />
            <SourcesSection sources={sources} />
            <SkillsSection skills={skills} />
            <ComputerUseSection
              browsedPages={browsedPages}
              liveObjects={conversationLiveDocs.filter(
                (doc) => doc.kind !== "space"
              )}
              onOpenDesktop={() => void openSpace()}
            />
          </>
        )}
      </div>
    </div>
  );
}

function useDetailsFiles({
  conversation,
  owner,
}: ConversationDetailsPanelProps): {
  files: DetailsFileItem[];
  workingFiles: DetailsFileItem[];
  isFilesLoading: boolean;
} {
  const { openPanel } = useConversationSidePanelContext();
  const usesFileSystem = conversation.metadata?.useFileSystem === true;

  const { sandboxFiles, isSandboxFilesLoading } = useConversationSandboxFiles({
    conversationId: conversation.sId,
    owner,
    options: { disabled: !usesFileSystem },
  });
  // A chat reads as one thread across its segments, so its files do too. The
  // current segment's own listing stays the live one (refreshed as the agent
  // writes); the chat listing adds the earlier segments' files.
  const { chatFiles } = useChatFiles({
    owner,
    chatId: conversation.chatId,
    disabled: !usesFileSystem,
  });
  const { attachments, isConversationAttachmentsLoading } =
    useConversationAttachments({
      conversationId: conversation.sId,
      owner,
      options: { disabled: usesFileSystem },
    });

  return useMemo(() => {
    const openFile = ({
      fileId,
      filePath,
      contentType,
    }: {
      fileId: string | null;
      filePath?: string;
      contentType: string;
    }) => {
      if (fileId && isInteractiveContentType(contentType)) {
        openPanel({ type: "interactive_content", fileId });
      } else {
        openPanel({
          type: "file_preview",
          fileId: fileId ?? undefined,
          filePath,
        });
      }
    };

    if (usesFileSystem) {
      const toItem = (entry: FileSystemFileEntry): DetailsFileItem => ({
        key: entry.path,
        title: isInteractiveContentType(entry.contentType)
          ? getFrameDisplayTitle(entry.fileName)
          : entry.fileName,
        contentType: entry.contentType,
        date: entry.lastModifiedMs,
        onClick: () =>
          openFile({
            fileId: entry.fileId,
            filePath: entry.path,
            contentType: entry.contentType,
          }),
      });
      const currentPaths = new Set(sandboxFiles.map((entry) => entry.path));
      const fileEntries = [
        ...sandboxFiles,
        ...chatFiles.filter((entry) => !currentPaths.has(entry.path)),
      ].filter((entry): entry is FileSystemFileEntry => !entry.isDirectory);
      return {
        files: fileEntries
          .filter((entry) => !isToolOutputPath(entry.path))
          .map(toItem),
        workingFiles: fileEntries
          .filter((entry) => isToolOutputPath(entry.path))
          .map(toItem),
        isFilesLoading: isSandboxFilesLoading,
      };
    }

    const toItem = (item: ConversationAttachmentItem): DetailsFileItem => {
      if (isFileAttachmentType(item)) {
        return {
          key: item.fileId,
          title: isInteractiveContentType(item.contentType)
            ? getFrameDisplayTitle(item.title)
            : item.title,
          contentType: item.contentType,
          date: item.updatedAt ?? item.createdAt ?? null,
          onClick: () =>
            openFile({
              fileId: item.fileId,
              filePath: item.path ?? undefined,
              contentType: item.contentType,
            }),
        };
      }
      if (isContentNodeAttachmentType(item)) {
        const { sourceUrl } = item;
        return {
          key: item.title,
          title: item.title,
          contentType: item.contentType,
          date: null,
          onClick: sourceUrl
            ? () => window.open(sourceUrl, "_blank", "noopener,noreferrer")
            : undefined,
        };
      }
      assertNever(item);
    };
    return {
      files: attachments.filter((a) => !a.hidden).map(toItem),
      workingFiles: [],
      isFilesLoading: isConversationAttachmentsLoading,
    };
  }, [
    usesFileSystem,
    sandboxFiles,
    chatFiles,
    isSandboxFilesLoading,
    attachments,
    isConversationAttachmentsLoading,
    openPanel,
  ]);
}

function FileRow({ file }: { file: DetailsFileItem }) {
  const { liveDocKind } = file;
  const tone: DocumentTone | undefined =
    liveDocKind ?? getArtifactTone(file.contentType, file.title);
  return (
    <DetailsRow
      visual={
        <Icon
          visual={
            liveDocKind
              ? liveDocIcon(liveDocKind)
              : getFileTypeIcon(file.contentType, file.title)
          }
          size="sm"
        />
      }
      tileClassName={tone && documentToneClassNames[tone].tile}
      title={file.title}
      subtitle={[
        liveDocKind
          ? liveDocKindLabel(liveDocKind)
          : getSingularFileCategoryLabelForContentType(file.contentType),
        file.date ? moment(file.date).fromNow() : null,
      ]
        .filter(Boolean)
        .join(" · ")}
      onClick={file.onClick}
    />
  );
}

function FaviconStack({ urls }: { urls: (string | undefined)[] }) {
  return (
    <span className="flex shrink-0 items-center">
      {urls.slice(0, faviconStackSize).map((url, index) => (
        <span
          key={index}
          className="-ml-1 flex h-4 w-4 items-center justify-center rounded-full border border-border bg-background first:ml-0"
        >
          <FaviconIcon websiteUrl={url} size="sm" />
        </span>
      ))}
    </span>
  );
}

function FilesSection({
  files,
  workingFiles,
}: {
  files: DetailsFileItem[];
  workingFiles: DetailsFileItem[];
}) {
  const { openPanel } = useConversationSidePanelContext();

  return (
    <DetailsSection
      title="Files"
      items={files}
      count={files.length + workingFiles.length}
      renderItem={(file) => <FileRow file={file} />}
      onShowAll={() => openPanel({ type: "files" })}
      emptyState={
        workingFiles.length === 0 && (
          <DetailsEmptyState
            visual={File06}
            title="No files yet"
            description="Files you share or the agent creates show up here."
          />
        )
      }
      footer={
        workingFiles.length > 0 && (
          <div className="pt-1">
            <DetailsSection
              title="Agent working files"
              items={workingFiles}
              defaultOpen={false}
              renderItem={(file) => <FileRow file={file} />}
              emptyState={null}
            />
          </div>
        )
      }
    />
  );
}

function SourcesSection({ sources }: { sources: ConversationSourceType[] }) {
  const groups = useMemo(() => groupSourcesByProvider(sources), [sources]);

  return (
    <DetailsSection
      title="Sources"
      items={groups}
      count={sources.length}
      renderItem={(group) => <SourceGroupRow group={group} />}
      emptyState={
        <DetailsEmptyState
          visual={BookOpen01}
          title="No sources yet"
          description="Websites and documents the agent cites show up here."
        />
      }
    />
  );
}

function SourceGroupRow({ group }: { group: SourceGroup }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const count = group.sources.length;
  const isWeb = group.provider === "webcrawler";

  return (
    <div>
      <DetailsRow
        visual={
          <IconForAttachmentCitation
            provider={group.provider}
            sourceUrl={group.sources[0].href}
            size="sm"
          />
        }
        title={group.label}
        subtitle={
          <span className="flex items-center gap-1.5">
            {isWeb && (
              <FaviconStack urls={group.sources.map(({ href }) => href)} />
            )}
            {isWeb ? `${count} visited` : `${count} used`}
          </span>
        }
        onClick={() => setIsExpanded((value) => !value)}
      />
      <DetailsCollapse open={isExpanded}>
        <SourceList sources={group.sources} />
      </DetailsCollapse>
    </div>
  );
}

function SourceList({ sources }: { sources: ConversationSourceType[] }) {
  return (
    <ul className="ml-[18px] flex flex-col border-l border-border pl-3">
      {sources.map((source) => {
        const { href } = source;
        return (
          <li key={href ?? source.title}>
            <DetailsRow
              visual={
                <IconForAttachmentCitation
                  provider={source.provider}
                  contentType={source.contentType}
                  sourceUrl={href}
                  size="sm"
                />
              }
              title={source.title}
              subtitle={href?.replace(urlProtocolPattern, "")}
              onClick={
                href
                  ? () => window.open(href, "_blank", "noopener,noreferrer")
                  : undefined
              }
            />
          </li>
        );
      })}
    </ul>
  );
}

function SkillsSection({ skills }: { skills: ConversationDetailsSkillType[] }) {
  const { openPanel } = useConversationSidePanelContext();

  return (
    <DetailsSection
      title="Skills"
      items={skills}
      count={skills.filter(({ used }) => used).length}
      layout="chips"
      renderItem={(skill) => (
        <DetailsChip
          visual={<Icon visual={getSkillIcon(skill.icon)} size="xs" />}
          label={skill.name}
          muted={!skill.used}
          onClick={() => openPanel({ type: "skill", skillId: skill.sId })}
        />
      )}
      emptyState={
        <DetailsEmptyState
          visual={Stars01}
          title="No skills enabled"
          description="Skills the agent uses in this chat show up here."
        />
      }
    />
  );
}

type ComputerUseItem =
  | { type: "desktop" }
  | { type: "browser"; pages: ConversationBrowsedPageType[] };

// The conversation's desktop comes first: every live object the people and agents made here,
// laid out as windows with a dock to add more.
function ComputerUseSection({
  browsedPages,
  liveObjects,
  onOpenDesktop,
}: {
  browsedPages: ConversationBrowsedPageType[];
  liveObjects: LiveDocType[];
  onOpenDesktop: () => void;
}) {
  const { isDark } = useTheme();
  const liveObjectCount = liveObjects.length;
  const [isExpanded, setIsExpanded] = useState(false);
  const count = browsedPages.length;
  const items: ComputerUseItem[] = [
    { type: "desktop" },
    ...(count > 0 ? [{ type: "browser" as const, pages: browsedPages }] : []),
  ];

  return (
    <DetailsSection
      title="Computer use"
      items={items}
      count={liveObjectCount + count}
      renderItem={(item) => {
        switch (item.type) {
          case "desktop":
            return (
              <div className="pb-1 pt-1">
                <DesktopPreview
                  objects={liveObjects.map(({ kind, title }) => ({
                    kind,
                    title,
                  }))}
                  isDark={isDark}
                  title="Desktop"
                  subtitle={
                    liveObjectCount > 0
                      ? `${liveObjectCount} live ${liveObjectCount === 1 ? "object" : "objects"}`
                      : "Docs, sheets, browsers and calls, side by side"
                  }
                  onOpen={onOpenDesktop}
                />
              </div>
            );
          case "browser":
            return (
              <div>
                <DetailsRow
                  visual={<Icon visual={Browser} size="sm" />}
                  title="Browser"
                  subtitle={
                    <span className="flex items-center gap-1.5">
                      <FaviconStack urls={item.pages.map(({ url }) => url)} />
                      {`${count} ${count === 1 ? "page" : "pages"} visited`}
                    </span>
                  }
                  onClick={() => setIsExpanded((value) => !value)}
                />
                {isExpanded && <BrowsedPageList browsedPages={item.pages} />}
              </div>
            );
          default:
            assertNeverAndIgnore(item);
            return null;
        }
      }}
      emptyState={null}
    />
  );
}

function BrowsedPageList({
  browsedPages,
}: {
  browsedPages: ConversationBrowsedPageType[];
}) {
  const { openPanel } = useConversationSidePanelContext();

  return (
    <ul className="ml-[18px] flex flex-col border-l border-border pl-3">
      {browsedPages.map((page) => (
        <li key={page.actionId}>
          <DetailsRow
            visual={<FaviconIcon websiteUrl={page.url} size="md" />}
            title={page.url.replace(urlProtocolPattern, "")}
            subtitle={moment(page.createdAt).fromNow()}
            onClick={() =>
              openPanel({
                type: "actions",
                messageId: page.messageId,
                actionId: page.actionId,
              })
            }
          />
        </li>
      ))}
    </ul>
  );
}
